/**
 * Performance Module — L'Agenda
 * Proxy premier parti (/api/ping) → GA4 Measurement Protocol.
 * Noms volontairement neutres pour contourner les filter lists adblockers.
 *
 * Contrat GA4 MP (durci le 2026-08-30, chantier tracking P0) :
 *  - Aucun snippet gtag.js n'est chargé : TOUT part par le Measurement
 *    Protocol. GA4 ne reconstitue donc ni sessions ni temps d'engagement
 *    tout seul → chaque event porte `session_id`, `session_number` et un
 *    `engagement_time_msec` réel (temps visible cumulé depuis l'event
 *    précédent). Sans ça : Sessions, durée moyenne, pages/session vides.
 *  - Jamais de nom réservé GA4 (session_start, first_visit, user_engagement…) :
 *    ces events sont rejetés en silence. Le démarrage de visite s'appelle
 *    `visit_start` et porte l'origine (page_referrer, referrer_host, utm_*).
 *  - `user_id` / `user_properties` vont dans le corps MP — les anciens
 *    gtag('config'/'set') poussaient dans un dataLayer que personne ne lisait.
 *    Branché sur window.LAgendaAuth (auth-state.js).
 *  - Géo + appareil : ajoutés côté collectAnalytics (user_location tiré de
 *    l'IP en France, sinon ip_override ; user_agent), sinon GA4 voit l'IP de
 *    la Cloud Function pour tout le monde.
 *  - Ville choisie sur le site : propriété utilisateur `ville_choisie` sur
 *    chaque envoi (lue dans le localStorage de js/villes.js).
 *
 * Testé par tests/frontend/test_perf_tracking.mjs (vrai module derrière un
 * stub DOM, payloads interceptés sur navigator.sendBeacon).
 */

const MP_ENDPOINT = '/api/ping';

// Client ID persistant (simulation du cookie _ga).
// TTL 13 mois pour aligner sur le cookie _ga natif de GA4 (expiration
// 2 ans cote GA, mais politique RGPD standard = 13 mois). Apres expiration
// un nouveau cid est genere : l'user est traite comme "nouveau visiteur"
// cote GA4, en conformite avec les guidelines CNIL. Audit data 2026-04-17
// Lot P3-3.
const GA_CID_TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 mois

// Session : 30 min d'inactivité, comme la session GA4 native (_ga_ses).
// localStorage et non sessionStorage : une session GA4 est par navigateur,
// pas par onglet — ouvrir un concert dans un nouvel onglet ne doit pas
// compter une deuxième visite.
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function lsGet(key) {
    try { return localStorage.getItem(key); } catch (_) { return null; }
}
function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (_) { /* mode privé restrictif */ }
}

function getClientId() {
    const cid = lsGet('_ga_cid');
    const created = parseInt(lsGet('_ga_cid_created') || '0', 10);
    const now = Date.now();
    if (cid && created && (now - created) < GA_CID_TTL_MS) return cid;
    // Soit absent, soit expiré -> générer un nouveau cid
    const fresh = Math.random().toString(36).substr(2) + '.' + now;
    lsSet('_ga_cid', fresh);
    lsSet('_ga_cid_created', String(now));
    return fresh;
}

const CLIENT_ID = getClientId();

// ============================================================
// SESSION (session_id / session_number)
// Limite connue : une session expirée pendant que la page reste ouverte
// n'est renouvelée qu'au prochain chargement — GA4 natif ferait pareil
// à la précision près du cookie _ga_ses.
// ============================================================
let SESSION_ID = '';
let SESSION_NUMBER = 1;
let SESSION_IS_NEW = false;

(function initSession() {
    const now = Date.now();
    const sid = lsGet('_ga_sid');
    const last = parseInt(lsGet('_ga_sid_last') || '0', 10);
    if (sid && last && (now - last) < SESSION_TIMEOUT_MS) {
        SESSION_ID = sid;
        SESSION_NUMBER = parseInt(lsGet('_ga_sn') || '1', 10) || 1;
        SESSION_IS_NEW = false;
    } else {
        SESSION_ID = String(Math.floor(now / 1000));
        SESSION_NUMBER = (parseInt(lsGet('_ga_sn') || '0', 10) || 0) + 1;
        SESSION_IS_NEW = true;
        lsSet('_ga_sid', SESSION_ID);
        lsSet('_ga_sn', String(SESSION_NUMBER));
    }
    lsSet('_ga_sid_last', String(now));
})();

function touchSession() {
    lsSet('_ga_sid_last', String(Date.now()));
}

// ============================================================
// ENGAGEMENT RÉEL
// Temps pendant lequel l'onglet est visible, cumulé, remis à zéro à chaque
// envoi : GA4 additionne les engagement_time_msec de tous les events d'une
// session pour calculer le temps d'engagement.
// ============================================================
let _engAccum = 0;
let _engSince = document.visibilityState === 'hidden' ? null : Date.now();

function pauseEngagement() {
    if (_engSince !== null) {
        _engAccum += Date.now() - _engSince;
        _engSince = null;
    }
}
function resumeEngagement() {
    if (_engSince === null) _engSince = Date.now();
}
// Prélève le cumul et le remet à zéro. Minimum 1 ms : un event sans
// engagement_time_msec ne compte pas dans les métriques d'engagement.
function takeEngagement() {
    const now = Date.now();
    if (_engSince !== null) {
        _engAccum += now - _engSince;
        _engSince = now;
    }
    const value = Math.round(_engAccum);
    _engAccum = 0;
    return Math.max(1, value);
}

// ============================================================
// IDENTITÉ (corps MP : user_id + user_properties)
// ============================================================
let _userId = null;
let _userProps = null;

// Ville dont la personne consulte l'agenda : js/villes.js la garde dans
// localStorage (choix dans la liste, « Autour de moi », lien de ville, ou
// ville posée par l'IP à la première visite). Propriété utilisateur sur
// CHAQUE envoi : toute l'audience se ventile alors par ville choisie, pas
// seulement les events `ville_choisie` (un par changement). Relue à chaque
// envoi : un changement de ville vaut dès l'event suivant. Absente tant que
// rien n'est choisi (visiteur arrivé sur une fiche concert par Google).
// GA4 plafonne une valeur de propriété utilisateur à 36 caractères (le plus
// long slug de villes, scey-sur-saone-et-saint-albin, en fait 29).
const VILLE_KEY = 'lagenda:ville';
const VILLE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function villeChoisie() {
    const v = lsGet(VILLE_KEY);
    return v && v.length <= 36 && VILLE_RE.test(v) ? v : null;
}

// ============================================================
// ENVOI VIA MEASUREMENT PROTOCOL (premier parti, toujours)
// ============================================================
function sendViaMeasurementProtocol(eventName, params = {}) {
    try {
        const payload = {
            client_id: CLIENT_ID,
            events: [{
                name: eventName,
                params: {
                    ...params,
                    session_id: SESSION_ID,
                    session_number: SESSION_NUMBER,
                    engagement_time_msec: params.engagement_time_msec || takeEngagement(),
                    page_location: params.page_location || window.location.href,
                    page_title: params.page_title || document.title
                }
            }]
        };
        if (_userId) payload.user_id = _userId;
        const props = { ..._userProps };
        const ville = villeChoisie();
        if (ville) props.ville_choisie = { value: ville };
        if (Object.keys(props).length) payload.user_properties = props;
        const body = JSON.stringify(payload);
        touchSession();
        // sendBeacon est non-bloquant et survit à la fermeture de page
        if (navigator.sendBeacon) {
            navigator.sendBeacon(MP_ENDPOINT, new Blob([body], { type: 'application/json' }));
        } else {
            fetch(MP_ENDPOINT, { method: 'POST', body, keepalive: true }).catch(() => {});
        }
    } catch (e) { /* silencieux */ }
}

// ============================================================
// HELPER PRINCIPAL — toujours via MP
// ============================================================
export function trackEvent(name, params = {}) {
    sendViaMeasurementProtocol(name, params);
}

window.trackEvent = trackEvent;

// ============================================================
// ORIGINE DE LA VISITE
// page_referrer brut (c'est ce que GA4 lit pour source/medium) + hôte
// séparé pour lecture directe + utm_* de l'URL d'atterrissage.
// ============================================================
function referrerInfo() {
    const info = {};
    try {
        if (document.referrer) {
            info.page_referrer = document.referrer;
            info.referrer_host = new URL(document.referrer).hostname;
        } else {
            info.referrer_host = 'direct';
        }
    } catch (_) { info.referrer_host = 'direct'; }
    try {
        const q = new URLSearchParams(window.location.search);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
            const v = q.get(k);
            if (v) info[k] = v.slice(0, 100);
        });
    } catch (_) {}
    return info;
}

const ORIGIN = referrerInfo();

// ============================================================
// IDENTIFICATION UTILISATEUR
// ============================================================
function setUserIdentity(user, extra = {}) {
    try {
        if (user) {
            _userId = user.uid;
            const provider = (user.providerData && user.providerData[0] && user.providerData[0].providerId) || 'unknown';
            _userProps = {
                user_type: { value: 'signed_in' },
                plan: { value: extra.plan || 'free' },
                auth_provider: { value: provider }
            };
            const meta = user.metadata || {};
            const isNewUser = !!(meta.creationTime && meta.lastSignInTime &&
                Math.abs(new Date(meta.creationTime) - new Date(meta.lastSignInTime)) < 5000);
            // login / sign_up : une fois par session, pas à chaque page chargée
            // (onAuthStateChanged rejoue l'utilisateur à chaque navigation).
            if (lsGet('_ga_auth_sid') !== SESSION_ID) {
                lsSet('_ga_auth_sid', SESSION_ID);
                trackEvent(isNewUser ? 'sign_up' : 'login', { method: provider });
            }
        } else {
            _userId = null;
            _userProps = { user_type: { value: 'anonymous' } };
        }
    } catch (e) {}
}

window.setUserIdentity = setUserIdentity;

// Branchement sur auth-state.js. onChange rappelle immédiatement avec
// l'utilisateur courant (null tant que Firebase n'a pas répondu).
function wireAuth() {
    const auth = window.LAgendaAuth;
    if (!auth || typeof auth.onChange !== 'function') return false;
    auth.onChange(user => setUserIdentity(user));
    return true;
}
if (!wireAuth()) window.addEventListener('lagenda-auth-ready', wireAuth, { once: true });

// ============================================================
// 1. DÉMARRAGE DE VISITE (nouvelle session uniquement)
// `session_start` est un nom réservé GA4 → rejeté. D'où `visit_start`.
// ============================================================
if (SESSION_IS_NEW) {
    try {
        trackEvent('visit_start', {
            ...ORIGIN,
            landing_page: window.location.pathname,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            language: navigator.language,
            device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
        });
    } catch (e) {}
}

// page_view initial — envoyé par nous via MP (aucun snippet gtag ne le fait)
function sendInitialPageView() {
    trackEvent('page_view', {
        ...ORIGIN,
        page_location: window.location.href,
        page_title: document.title
    });
}
// Accueil : js/villes.js peut renvoyer la personne vers sa ville (/lyon/). La
// vue est alors celle de /lyon/, pas une de « / » en plus : on attend sa
// décision (LAgendaVilles.ready ne se résout jamais quand l'accueil part).
// Délai borné : sans réponse de l'index des villes, la vue de « / » part.
// visit_start, lui, garde « / » comme page d'arrivée (c'est vrai).
function initialPageView() {
    const villes = window.LAgendaVilles;
    if (window.location.pathname !== '/' || !villes || !villes.ready) return sendInitialPageView();
    let sent = false;
    const once = () => { if (!sent) { sent = true; sendInitialPageView(); } };
    villes.ready.then(once, once);
    setTimeout(once, 4000);
}
// villes.js est un script `defer` placé après ce module : il est exécuté
// avant DOMContentLoaded.
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialPageView, { once: true });
else initialPageView();

// File d'attente pour les scripts qui parlent avant nous (defer/module en
// ordre de document, fetch asynchrones…) : ils poussent [name, params] dans
// window.__trackQueue ; on rejoue, puis la file devient un passe-plat.
try {
    const pending = Array.isArray(window.__trackQueue) ? window.__trackQueue : [];
    window.__trackQueue = { push: (item) => { try { trackEvent(item[0], item[1] || {}); } catch (_) {} } };
    pending.forEach(item => trackEvent(item[0], item[1] || {}));
} catch (_) {}

// ============================================================
// 2. NAVIGATION SPA via hashchange
// Convertit le hash fragment en chemin virtuel pour que GA4 voie
// des pages distinctes (ex: "#salle/l-olympia" → "/salle/l-olympia/")
// Sans conversion, tout s'écrase sur "/" car GA4 ignore le fragment #.
// ============================================================
function hashToVirtualPath(hash) {
    if (!hash || hash === '#') return '/';
    const h = hash.replace(/^#/, ''); // retire le #
    if (h.startsWith('salle/'))   return '/salle/'   + h.slice(6).replace(/\/$/, '') + '/';
    if (h.startsWith('genre/'))   return '/genre/'   + h.slice(6).replace(/\/$/, '') + '/';
    if (h.startsWith('artiste/')) return '/artiste/' + h.slice(8).replace(/\/$/, '') + '/';
    if (h.startsWith('concert/')) return '/concert/' + h.slice(8).replace(/\/$/, '') + '/';
    if (h === 'calendar')         return '/calendrier/';
    if (h === 'stats')            return '/statistiques/';
    return '/' + h + '/';
}

window.addEventListener('hashchange', () => {
    const virtualPath = hashToVirtualPath(window.location.hash);
    trackEvent('page_view', {
        page_title: document.title,
        page_location: window.location.origin + virtualPath
    });
});

// ============================================================
// 3. SCROLL DEPTH
// ============================================================
const _scrollSeen = new Set();
window.addEventListener('scroll', () => {
    try {
        const scrollable = document.body.scrollHeight - window.innerHeight;
        if (scrollable <= 0) return;
        const pct = Math.round((window.scrollY / scrollable) * 100);
        [25, 50, 75, 90].forEach(milestone => {
            if (pct >= milestone && !_scrollSeen.has(milestone)) {
                _scrollSeen.add(milestone);
                trackEvent('scroll_depth', { percent: milestone });
            }
        });
    } catch (e) {}
}, { passive: true });

// ============================================================
// 4. PALIERS DE TEMPS (30 / 60 / 180 s visibles) — lecture rapide dans
// les rapports ; le temps exact vient de engagement_time_msec.
// ============================================================
let _engagementTimer = null;
const _engagementSent = new Set();

function scheduleEngagement(seconds) {
    if (_engagementSent.has(seconds)) return;
    _engagementTimer = setTimeout(() => {
        if (!_engagementSent.has(seconds)) {
            _engagementSent.add(seconds);
            trackEvent('time_on_site', { seconds });
            const next = seconds === 30 ? 60 : seconds === 60 ? 180 : null;
            if (next) scheduleEngagement(next);
        }
    }, seconds * 1000);
}
scheduleEngagement(30);

// ============================================================
// 5. FIN DE PAGE — vide le compteur d'engagement quand l'onglet part en
// arrière-plan ou se ferme. Sans ça, le temps passé depuis le dernier
// event est perdu (c'est souvent l'essentiel d'une lecture de page).
// ============================================================
function flushEngagement() {
    pauseEngagement();
    if (_engAccum >= 1000) {
        trackEvent('page_engagement', { engagement_time_msec: takeEngagement() });
    }
}

document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        clearTimeout(_engagementTimer);
        flushEngagement();
    } else {
        resumeEngagement();
        const next = _engagementSent.has(180) ? null
                   : _engagementSent.has(60)  ? 180
                   : _engagementSent.has(30)  ? 60
                   : 30;
        if (next) scheduleEngagement(next);
    }
});
// Safari iOS ne déclenche pas toujours visibilitychange à la fermeture.
window.addEventListener('pagehide', flushEngagement);

// ============================================================
// 6. CLICS SORTANTS — délégué en phase de capture : passe avant les
// stopPropagation des cartes, et sendBeacon survit à l'ouverture de
// l'onglet. La billetterie est LE clic qui compte (ticket_click) ; tout
// autre lien externe part en outbound_click (site de salle, Spotify…).
// Le placement vient de la classe/id du lien tel que rendu par
// design-home.js, concert.js et generate_seo_pages.py.
//
// Plateforme, salle et genre (ticket_platform / venue / genre) sont lus
// sur les attributs data-platform / data-venue / data-genre que ces mêmes
// rendus posent sur le lien (ou un ancêtre). Le domaine du lien ne suffit
// pas : lacigale.fnacspectacles.com est Fnac, billetterie.accorarena.com
// est Ticketmaster en marque blanche. La source est la colonne
// `ticketing_platform` de la base — jamais une table de domaines dupliquée
// ici, qui dériverait de scrapers/ticketing_utils.py.
// ============================================================
const TICKET_PLACEMENTS = [
    ['card-billet', 'card'],           // chip « Billet ↗ » des cartes (SPA + pages SEO)
    ['br', 'preview'],                 // panneau latéral de la home
    ['hero-billet', 'hero'],           // hero de la home
    ['seo-cta', 'page_cta'],           // CTA des pages /concert/ pré-rendues
    ['event-ticket-link', 'list'],     // listes des hubs SEO
    ['primary', 'concert_page']        // CTA de concert.html
];

function ticketPlacement(a) {
    for (const [key, placement] of TICKET_PLACEMENTS) {
        if (a.id === key || (a.classList && a.classList.contains(key))) return placement;
    }
    return /billet|r[ée]server|ticket|revente/i.test(a.textContent || '') ? 'other' : null;
}

function ticketMeta(a) {
    const out = {};
    try {
        const pick = (key) => {
            const own = a.dataset && a.dataset[key];
            if (own) return own;
            const host = typeof a.closest === 'function' ? a.closest('[data-' + key + ']') : null;
            return (host && host.dataset && host.dataset[key]) || '';
        };
        const platform = pick('platform');
        const venue = pick('venue');
        const genre = pick('genre');
        if (platform) out.ticket_platform = platform;
        if (venue) out.venue = venue;
        if (genre) out.genre = genre;
    } catch (_) {}
    return out;
}

function concertIdFor(a) {
    try {
        const host = a.closest('[data-id],[data-event-id]');
        if (host && host.dataset) return host.dataset.id || host.dataset.eventId || '';
        return (document.body && document.body.dataset && document.body.dataset.concertId) || '';
    } catch (_) { return ''; }
}

document.addEventListener('click', (ev) => {
    try {
        const target = ev.target;
        const a = target && typeof target.closest === 'function' ? target.closest('a[href]') : null;
        if (!a || !a.href) return;
        const url = new URL(a.href, window.location.href);
        if (!/^https?:$/.test(url.protocol) || url.hostname === window.location.hostname) return;
        const placement = ticketPlacement(a);
        const params = {
            link_domain: url.hostname,
            // GA4 limite une valeur de paramètre à 100 caractères (page_location
            // fait exception). Le validateur /debug/mp/collect ne le signale pas :
            // on tronque nous-mêmes plutôt que de laisser GA4 décider en silence.
            link_url: url.href.slice(0, 100),
            link_text: (a.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60)
        };
        if (placement) {
            params.placement = placement;
            params.concert_id = concertIdFor(a);
            Object.assign(params, ticketMeta(a));
        }
        trackEvent(placement ? 'ticket_click' : 'outbound_click', params);
    } catch (_) {}
}, true);
